import h from 'hyperscript';
import isEqual from 'lodash-es/isEqual';

import { CONDITIONS_HIERARCHIES_CHANGE } from 'cadenza/conditions/event-types';
import { DecimalFormat } from 'cadenza/utils/decimal-format/decimal-format';
import { on, unByKey } from 'cadenza/utils/event-util';
import { ConditionApi } from 'cadenza/api-client/conditions/condition-api';
import { assert } from 'cadenza/utils/custom-error';
import { logger } from 'cadenza/utils/logging';
import { ConditionElement } from 'cadenza/conditions/condition-element';
import {
  fromNonLocalizedTextValueToNumberIfProceed
} from 'cadenza/conditions/interval-condition/interval-condition-numeric-converter';

import './interval-condition.css';
import i18n from './messages.properties';

export class IntervalCondition extends ConditionElement {

  #decimalFormat;
  #isDuration;
  #minStart;
  #minStartFormatted;
  #maxEnd;
  #maxEndFormatted;
  #startContextSensitive;
  #endContextSensitive;

  #dataComponentValue; // keeps values in JSF based implementation
  #conditionApiSupplier;
  #limitsConfiguration;
  #allowedValues;
  #value;
  #defaultValue;

  #latestSetValuesPromise; // for keeping consistency for other conditions, see onConditionChange

  #eventKeys;

  constructor (options = {
    conditionApiSupplier: configObject => new ConditionApi(configObject)
  }) {
    super();
    const { limitsConfiguration, allowedValues, values, conditionApiSupplier } = options;
    this.#limitsConfiguration = limitsConfiguration;
    this.#allowedValues = allowedValues;
    this.#value = values?.valueItems[0];
    assert(conditionApiSupplier, 'conditionApiSupplier is mandatory');
    this.#conditionApiSupplier = conditionApiSupplier;
  }

  connectedCallback () {
    super.connectedCallback();
    this.conditionApi = this.#conditionApiSupplier({
      conditionSetId: this.conditionSetId,
      conditionName: this.conditionName,
      conditionId: this.conditionId,
      conditionValuesSetHashGetter: () => this.conditionValuesSetHash
    });
    this.classList.add('interval-condition');

    this.#dataComponentValue = this.previousElementSibling?.value;
    this.#isDuration = this.inputType === 'Duration';
    if (this.limitsConfiguration) {
      this.#decimalFormat = new DecimalFormat(this.limitsConfiguration.inputFormat);
    }
    this.#minStart = this.allowedValues.start;
    this.#minStartFormatted = this.allowedValues.startFormatted;
    this.#maxEnd = this.allowedValues.end;
    this.#maxEndFormatted = this.allowedValues.endFormatted;
    this.#startContextSensitive = this.limitsConfiguration.start.contextSensitive;
    this.#endContextSensitive = this.limitsConfiguration.end.contextSensitive;

    this.#eventKeys = [];
    this._configureContextSensitiveHandling();

    let leftPlaceHolder = '';
    if (this.#minStartFormatted) {
      leftPlaceHolder = '>=' + this.#minStartFormatted;
    }
    let rightPlaceHolder = '';
    if (this.#maxEndFormatted) {
      rightPlaceHolder = '<=' + this.#maxEndFormatted;
    }

    this._leftInput = h('input.interval.form-control', { type: 'text', placeholder: leftPlaceHolder, ariaLabel: i18n('input.initialValue.ariaLabel', { filterName: this.conditionLabel }) });
    this._rightInput = h('input.interval.form-control ', { type: 'text', placeholder: rightPlaceHolder, ariaLabel: i18n('input.finalValue.ariaLabel', { filterName: this.conditionLabel }) });

    this.append(
      h('div.form',
        this._leftInput,
        h('span.valueIntervalLabel', i18n('to')),
        this._rightInput
      ));
    const disabled = !this.enabled;
    this._leftInput.disabled = disabled;
    this._rightInput.disabled = disabled;
    if (this.format) {
      this.append(h('div.inputFieldFormatInfo', i18n('formatLabel'), ':', this.format));
    }
    const startValue = this.value?.startFormatted;
    const endValue = this.value?.endFormatted;
    if (startValue !== undefined && startValue !== null) {
      this.left = startValue;
    }
    if (endValue !== undefined && endValue !== null) {
      this.right = endValue;
    }
    this.#defaultValue = this.#dataComponentValue?.defaultValue;
    this.value = this.#value ?? this.#dataComponentValue?.value;
    this.initChanged();
    setTimeout(() => {
      // BUG: It turns out that for intervals no error is shown, as the range appears valid,
      // even though it isn't. (Tested with context-sensitive condition ranges.)
      this._validateParsedInputs({
        left: this.value?.start,
        right: this.value?.end
      });
    });

    this._assignEventListeners();
  }

  disconnectedCallback () {
    unByKey(this.#eventKeys);
    this.innerHTML = '';
  }

  /**
   * This method allows to configure condition/hierarchy change handling
   * in case of condition's context sensitivity.
   *
   * @protected
   */
  _configureContextSensitiveHandling () {
    if ((this.#startContextSensitive || this.#endContextSensitive) && this.filterController) {
      this.#eventKeys.push(
        on(this.filterController, CONDITIONS_HIERARCHIES_CHANGE, event => this._filterCriteriaChanged(event))
      );
    }
  }

  _assignEventListeners () {
    this.#eventKeys.push(
      on(this._leftInput, 'change', event => this._reactToChange(event)),
      on(this._rightInput, 'change', event => this._reactToChange(event)),
      on(this._leftInput, 'blur', event => this._reactToFocus(event)),
      on(this._rightInput, 'blur', event => this._reactToFocus(event)),
      on(this._leftInput, 'keyup', event => this._keySubmit(event)),
      on(this._rightInput, 'keyup', event => this._keySubmit(event))
    );
  }

  onConditionChange () {
    try {
      if (!this.#isDuration) {
        this._validateNumber(this.left);
        this._validateNumber(this.right);
        const leftValueAsNumber = this._parseValue(this.left, false);
        const rightValueAsNumber = this._parseValue(this.right, false);
        this._validateSpread(leftValueAsNumber, rightValueAsNumber);
      }
    } catch (e) {
      if (e.validity) {
        this.reportValidity(e.validity);
        return;
      } else {
        logger.error('Failed to validate interval ondition range limits.', e);
      }
    }
    // the filter controller is not availabe in workbooks
    this.filterController?.disableOtherConditionsIfNeededForConsistency([ this ]);

    const leftValue = this._getLeftParsed();
    const rightValue = this._getRightParsed();
    if (this.#isDuration && (leftValue != null || rightValue != null)) {
      const setValuesPromise = this.conditionApi.setValue([ [ leftValue, rightValue ] ]);
      this.#latestSetValuesPromise = setValuesPromise;
      setValuesPromise
        .then(json => {
          super.onConditionChange(json);
          return this.conditionApi.getValue();
        })
        .then(conditionValue => {
          const leftParsed = conditionValue.values[0][0];
          const rightParsed = conditionValue.values[0][1];
          return this._validateInputs(leftParsed, rightParsed);
        })
        .catch(error => {
          if (error.key && error.key === 'input-not-valid') {
            this.reportValidity({
              title: '',
              message: error.title,
              type: 'error',
              wrongDurationFormat: true,
              valid: false
            });
          } else {
            this.handleConditionValueChangeError(error);
          }
        })
        .finally(() => {
          this._rightChanged = false;
          this._leftChanged = false;
          // if the changes are not fully processed yet, disable the other conditions again.
          const enable = setValuesPromise === this.#latestSetValuesPromise;
          // the filter controller is not availabe in workbooks
          this.filterController?.setConditionsDisabledForConsistency(enable, this);
        });
    } else {
      const setValuesPromise = this._validateInputs(leftValue, rightValue);
      this.#latestSetValuesPromise = setValuesPromise;
      setValuesPromise
        .then(result => this.conditionApi.setValue([ [ result.left, result.right ] ]))
        .then(json => super.onConditionChange(json))
        .then(() => {
          this.left = leftValue;
          this.right = rightValue;
        })
        .catch(error => {
          if (error.key && error.key === 'input-not-valid') {
            this.reportValidity({
              title: '',
              message: error.title,
              type: 'error',
              wrongDurationFormat: true,
              valid: false
            });
          } else if (error.key && error.key === 'input-not-valid-client') {
            error.warn('interval not valid');
          } else {
            this.handleConditionValueChangeError(error);
          }
        })
        .finally(() => {
          this._rightChanged = false;
          this._leftChanged = false;
          // if the changes are not fully processed yet, disable the other conditions again.
          const enable = setValuesPromise === this.#latestSetValuesPromise;
          // the filter controller is not availabe in workbooks
          this.filterController?.setConditionsDisabledForConsistency(enable, this);
        });
    }
  }

  get enabled () {
    return super.enabled;
  }

  set enabled (enabled) {
    super.enabled = enabled;
    this._leftInput.disabled = !enabled;
    this._rightInput.disabled = !enabled;
  }

  _getRightParsed () {
    return this._parseValue(this.right);
  }

  _validateInputs (leftValue, rightValue) {
    return Promise.resolve({
      left: leftValue,
      right: rightValue
    }).then(result => this._validateParsedInputs(result));
  }

  _format (value) {
    if (this.#isDuration) {
      return value;
    } else {
      return _isInSupportedRange(value) ? this.#decimalFormat.format(value) : value;
    }
  }

  get left () {
    return this._leftInput.value.trim();
  }

  set left (value) {
    this._leftInput.value = this._format(value);
  }

  get right () {
    return this._rightInput.value.trim();
  }

  set right (value) {
    this._rightInput.value = this._format(value);
  }

  get value () {
    return { start: this._getLeftParsed(), end: this._getRightParsed() };
  }

  _getLeftParsed () {
    return this._parseValue(this.left);
  }

  initChanged () {
    this.changed = !isEqual(this.value?.start, this.#defaultValue?.start === undefined ? null : this.#defaultValue?.start)
      || !isEqual(this.value?.end, this.#defaultValue?.end === undefined ? null : this.#defaultValue?.end);
  }

  set value (value) {
    this.left = fromNonLocalizedTextValueToNumberIfProceed(value?.start, this.inputType);
    this.right = fromNonLocalizedTextValueToNumberIfProceed(value?.end, this.inputType);
  }

  get format () {
    return this.getAttribute('format');
  }

  get persistenceId () {
    return this.getAttribute('persistence-id');
  }

  _reactToFocus (event) {
    const querySelector = document.querySelector('.d-modal--form');
    if (querySelector && querySelector.contains(event.relatedTarget)) {
      // error message was shown, ignore it
      return;
    }
    if (!this._wasChanged()) {
      return;
    }
    if ((event.target === this._leftInput && event.relatedTarget !== this._rightInput)
      || (event.target === this._rightInput && event.relatedTarget !== this._leftInput)) {
      this.onConditionChange();
    }
  }

  _reactToChange (event) {
    if (event.target === this._leftInput) {
      this._leftChanged = true;
    }

    if (event.target === this._rightInput) {
      this._rightChanged = true;
    }
  }

  _wasChanged () {
    return this._leftChanged || this._rightChanged;
  }

  _keySubmit (event) {
    if (event.keyCode === 13) {
      event.stopPropagation();
      this.onConditionChange();
    }
  }

  _filterCriteriaChanged (event) {
    this.contextSensitiveValues = event.detail.eagerlyContextSensitiveData?.[this.persistenceId];
  }

  set contextSensitiveValues (contextSensitiveValues) {
    if (contextSensitiveValues.rows && contextSensitiveValues.rows.length) {
      contextSensitiveValues = {
        start: contextSensitiveValues.rows[0].values[0],
        startFormatted: contextSensitiveValues.rows[0].formatted[0],
        end: contextSensitiveValues.rows[0].values[1],
        endFormatted: contextSensitiveValues.rows[0].formatted[1]

      };
    }
    if ((this.#startContextSensitive || this.#endContextSensitive) && contextSensitiveValues) {
      const newMinStart = JSON.parse(contextSensitiveValues.start);
      if (this.#startContextSensitive && this.#minStart !== newMinStart) {
        this.#minStart = newMinStart;
        this.#minStartFormatted = (contextSensitiveValues.startFormatted);
        this._leftInput.placeholder = '>=' + this.#minStartFormatted;
      }

      const newMaxEnd = JSON.parse(contextSensitiveValues.end);
      if (this.#endContextSensitive && this.#maxEnd !== newMaxEnd) {
        this.#maxEnd = newMaxEnd;
        this.#maxEndFormatted = (contextSensitiveValues.endFormatted);
        this._rightInput.placeholder = '<=' + this.#maxEndFormatted;
      }
    }
  }

  getValidationTitleAndMessage (validity) {
    const result = super.getValidationTitleAndMessage(validity);
    if (result) {
      return result;
    }

    if (validity.startValueBelowRange || validity.endValueBelowRange) {
      return {
        title: '',
        message: i18n('condition.validity.belowRange.message', {
          minRange: this.#minStartFormatted
        }),
        type: 'error'
      };
    } else if (validity.startValueAboveRange || validity.endValueAboveRange) {
      return {
        title: '',
        message: i18n('condition.validity.aboveRange.message', {
          maxRange: this.#maxEndFormatted
        }),
        type: 'error'
      };
    } else if (validity.reversedOrder) {
      return {
        title: '',
        message: i18n('condition.validity.reversedOrder.message'),
        type: 'error'
      };
    } else if (validity.wrongFormat) {
      return {
        title: '',
        message: i18n('condition.validity.wrongFormat.message'),
        type: 'error'
      };
    } else if (validity.wrongDurationFormat) {
      return validity;
    }
  }

  _valueBelowRange (value) {
    if (this.#minStart && (value || value === 0)) {
      if (this._isRepresentedAsString()) {
        return _lowerThan(value, this.#minStart);
      }
      return value < this.#minStart;
    }
    return false;
  }

  _valueAboveRange (value) {
    if (this.#maxEnd && (value || value === 0)) {
      if (this._isRepresentedAsString()) {
        return _biggerThan(value, this.#maxEnd);
      }
      return value > this.#maxEnd;
    }
    return false;
  }

  _reversedOrder (left, right) {
    if (right && left) {
      if (this._isRepresentedAsString()) {
        return _biggerThan(left, right);
      }
      return left > right;
    }
    return false;
  }

  /**
   * Returns the parsed value (mostly numeric but string for longs and as it is for BigDecimals)
   * from the incoming object.
   *
   * Returns null if the textValue is empty and it is not a number
   * Returns the same value if it is already a number.
   * Returns a parsed number based on the input type:
   * long:
   *  Value with decimal points cut off; depending on the parameter "longsAndBigDecimalsAsString"
   *  either as string or as number
   * BigDecimal:
   *  Depending on the parameter "longsAndBigDecimalsAsString", either the text value as it is, or
   *  parsed as number
   * int:
   *  A Math floor rounded value (number)
   * double
   * The numeric version of the text value (number)
   *
   * @param {object} textValue - The value to be parsed to a number
   * @param {boolean} longsAndBigDecimalsAsString - if true, values of type "long" or "BigDecimal"
   *   are left as string in order to not lose precision ((e.g. makes sense if the result is passed
   *   to the backend); if false they are parsed to numbers (e.g. makes sense if the result is used
   *   for a numerical less/greater than comparison).
   * @return {string|number|null} - The parsed representation of the provided text value
   * @private
   */
  _parseValue (textValue, longsAndBigDecimalsAsString = true) {
    if (!textValue && typeof textValue !== 'number') {
      return null;
    }
    if (typeof textValue === 'number') {
      return textValue;
    }

    return this._parseNumberInputTypes(this.#decimalFormat.formatBack(textValue), longsAndBigDecimalsAsString);
  }

  _parseNumberInputTypes (textValue, longsAndBigDecimalsAsString) {
    switch (this.inputType) {
      case 'long':
        if (longsAndBigDecimalsAsString) {
          const decimalPointIndex = textValue.indexOf('.');
          return decimalPointIndex !== -1 ? textValue.substring(0, decimalPointIndex) : textValue;
        } else {
          return Math.floor(Number(textValue));
        }
      case 'BigDecimal':
        return longsAndBigDecimalsAsString ? textValue : Number(textValue);
      case 'int':
        return Math.floor(Number(textValue));
      case 'double':
        return Number(textValue);
    }
    return textValue;
  }

  _isRepresentedAsString () {
    return this.inputType === 'long' || this.inputType === 'BigDecimal';
  }

  get hasValue () {
    return (this.left !== null && this.left !== '') || (this.right !== null && this.right !== '');
  }

  _validateParsedInputs (result) {
    const startValueBelowRange = this._valueBelowRange(result.left);
    const startValueAboveRange = this._valueAboveRange(result.left);
    const endValueBelowRange = this._valueBelowRange(result.right);
    const endValueAboveRange = this._valueAboveRange(result.right);
    const reversedOrder = this._reversedOrder(result.left, result.right);
    const validationResult = {
      valid: !(startValueBelowRange || startValueAboveRange || endValueBelowRange || endValueAboveRange || reversedOrder),
      startValueBelowRange,
      startValueAboveRange,
      endValueBelowRange,
      endValueAboveRange,
      reversedOrder
    };

    const assign = Object.assign(result, { valid: validationResult.valid });
    if (!validationResult.valid) {
      this.reportValidity(validationResult);
      const error = new Error('Invalid input data');
      error.key = 'input-not-valid-client';
      throw error;
    }
    return assign;
  }

  _validateNumber (textRepresentation) {
    if (Number.isNaN(Number(this.#decimalFormat.formatBack(textRepresentation)))) {
      const error = new Error('Invalid input data');
      error.validity = {
        title: '',
        message: i18n('condition.validity.wrongFormat.number', {
          value: textRepresentation
        }),
        type: 'error',
        wrongDurationFormat: true,
        valid: false
      };
      throw error;
    }
  }

  _validateSpread (min, max) {
    // We'll display errors only if there are both values. Otherwise field is empty.
    if (min && max && min > max) {
      const error = new Error();
      error.validity = {
        reversedOrder: true
      };
      throw error;
    }
  }

  _createConfig () {
    return {
      limitsConfiguration: this.#limitsConfiguration,
      allowedValues: this.#allowedValues,
      value: this.#value
    };
  }

  get inputType () {
    return this.limitsConfiguration?.inputType;
  }

  get limitsConfiguration () {
    return this.#limitsConfiguration ?? this.#dataComponentValue?.limitsConfiguration;
  }

  get allowedValues () {
    return this.#allowedValues ?? this.#dataComponentValue?.allowedValues;
  }

  get contextSensitive () {
    // Can't use this.#startContextSensitive/...end... directly because the getter can get called
    // even before connectedCallback.
    const limitsConfiguration = this.limitsConfiguration;
    return limitsConfiguration?.start.contextSensitive
        || limitsConfiguration?.end.contextSensitive;
  }

}

function _isInSupportedRange (first) {
  if (!first) {
    return false;
  }
  return Number(first) < Number.MAX_SAFE_INTEGER && Number(first) > Number.MIN_SAFE_INTEGER;
}

function _biggerThan (first, second) {
  if (_isInSupportedRange(first) && _isInSupportedRange(second)) {
    return Number(first) > Number(second);
  }
  return false;
}

function _lowerThan (first, second) {
  if (_isInSupportedRange(first) && _isInSupportedRange(second)) {
    return Number(first) < Number(second);
  }
  return false;
}

customElements.define('d-interval-condition', IntervalCondition);
